import type {ReactNode} from "react";

export interface Column<T> {
  key: keyof T | string;
  header: string;
  render?: (row: T) => ReactNode;
  width?: string;
  align?: "left" | "center" | "right";
}

interface BaseTableProps<T extends {id: string | number}> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
}

const SKELETON_ROW_KEYS = ["sk0", "sk1", "sk2", "sk3", "sk4", "sk5"] as const;

const SkeletonRow = ({colKeys}: {colKeys: string[]}) => (
  <tr>
    {colKeys.map(k => (
      <td
        key={k}
        className="px-4 py-3"
      >
        <div className="h-4 bg-(--color-surface-2) rounded animate-pulse w-3/4" />
      </td>
    ))}
  </tr>
);

export const BaseTable = <T extends {id: string | number}>({
  columns,
  data,
  loading = false,
  emptyMessage = "No data available",
  onRowClick,
}: BaseTableProps<T>) => {
  const colKeys = columns.map(col => String(col.key));

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-(--color-border)">
            {columns.map(col => (
              <th
                key={String(col.key)}
                className={[
                  "px-4 py-3 text-xs font-semibold text-(--color-text-secondary) uppercase tracking-wide whitespace-nowrap",
                  col.align === "center" ? "text-center" : col.align === "right" ? "text-right" : "text-left",
                ].join(" ")}
                style={col.width ? {width: col.width} : undefined}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="divide-y divide-(--color-divider)">
          {loading ? (
            SKELETON_ROW_KEYS.map(key => (
              <SkeletonRow
                key={key}
                colKeys={colKeys}
              />
            ))
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-12 text-center text-(--color-text-tertiary)"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map(row => (
              <tr
                key={row.id}
                onClick={() => onRowClick?.(row)}
                className={[
                  "transition-colors duration-(--transition-fast)",
                  onRowClick ? "cursor-pointer hover:bg-(--color-surface-2)" : "",
                ].join(" ")}
              >
                {columns.map(col => (
                  <td
                    key={String(col.key)}
                    className={[
                      "px-4 py-3 text-(--color-text) whitespace-nowrap",
                      col.align === "center" ? "text-center" : col.align === "right" ? "text-right" : "text-left",
                    ].join(" ")}
                  >
                    {col.render ? col.render(row) : String(row[col.key as keyof T] ?? "—")}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
